import React from 'react';
import type { DataRow, ColumnDefinition, TabDefinition, FilterDefinition, QuickFilterDefinition } from '../types';

export interface UseCardTableParams {
  data: DataRow[];
  columns: ColumnDefinition[];
  tabs: TabDefinition[];
  filters?: FilterDefinition[];
  quickFilters?: QuickFilterDefinition[];
  /** Controls what the header Select All checkbox targets */
  selectAllScope?: 'page' | 'filtered' | 'all';
  controlledActiveTab?: string;
  controlledSortColumn?: string;
  controlledSortDirection?: 'asc' | 'desc';
  controlledCurrentPage?: number;
  controlledShowFilterBar?: boolean;
  itemsPerPage: number;
  onSort?: (column: string, direction: 'asc' | 'desc') => void;
  onTabChange?: (tabId: string) => void;
  onPageChange?: (page: number) => void;
  onSearch?: (query: string) => void;
  onFilterChange?: (id: string, value: any) => void;
  onFilterToggle?: () => void;
  onItemsPerPageChange?: (rows: number) => void;
}

export function useCardTable(params: UseCardTableParams) {
  const {
    data,
    columns,
    tabs,
    filters = [],
    quickFilters = [],
    selectAllScope = 'page',
    controlledActiveTab,
    controlledSortColumn,
    controlledSortDirection,
    controlledCurrentPage,
    controlledShowFilterBar = false,
    itemsPerPage,
    onSort,
    onTabChange,
    onPageChange,
    onSearch,
    onFilterChange,
    onFilterToggle,
    onItemsPerPageChange,
  } = params;

  const [internalActiveTab, setInternalActiveTab] = React.useState(
    controlledActiveTab ?? tabs[0]?.id ?? 'all'
  );
  const [internalSortColumn, setInternalSortColumn] = React.useState(
    controlledSortColumn ?? columns.find(c => c.sortable)?.key ?? columns[0]?.key ?? 'id'
  );
  const [internalSortDirection, setInternalSortDirection] = React.useState<'asc' | 'desc'>(
    controlledSortDirection ?? 'desc'
  );
  const [internalCurrentPage, setInternalCurrentPage] = React.useState(
    controlledCurrentPage ?? 1
  );
  const [internalSearch, setInternalSearch] = React.useState('');
  const [selectedRows, setSelectedRows] = React.useState<string[]>([]);
  const [expandedRows, setExpandedRows] = React.useState<string[]>([]);
  const [filterBarVisible, setFilterBarVisible] = React.useState(controlledShowFilterBar);
  const [pageSize, setPageSize] = React.useState(itemsPerPage);
  // Filter values keyed by filter id
  const [filterValues, setFilterValues] = React.useState<Record<string, any>>({});
  // Active quick filter chips
  const [activeQuickFilterIds, setActiveQuickFilterIds] = React.useState<string[]>([]);
  // Column visibility
  const [visibleColumnKeys, setVisibleColumnKeys] = React.useState<string[]>(
    () => columns.filter((c) => c.visible !== false).map((c) => c.key)
  );

  React.useEffect(() => {
    setPageSize(itemsPerPage);
  }, [itemsPerPage]);

  // Keep visible columns in sync if columns prop changes
  React.useEffect(() => {
    setVisibleColumnKeys((prev) => {
      const nextDefault = columns.filter((c) => c.visible !== false).map((c) => c.key);
      // if prev contains keys not in next, remove; if prev empty (first run) use next
      const validPrev = prev.filter((k) => columns.some((c) => c.key === k));
      return validPrev.length ? validPrev : nextDefault;
    });
  }, [columns]);

  const activeTab = controlledActiveTab ?? internalActiveTab;
  const sortColumn = controlledSortColumn ?? internalSortColumn;
  const sortDirection = controlledSortDirection ?? internalSortDirection;
  const currentPage = controlledCurrentPage ?? internalCurrentPage;

  const handleSort = (columnKey: string) => {
    const newDirection = sortColumn === columnKey && sortDirection === 'asc' ? 'desc' : 'asc';
    if (onSort) onSort(columnKey, newDirection);
    else {
      setInternalSortColumn(columnKey);
      setInternalSortDirection(newDirection);
    }
  };

  const handleTabChange = (tabId: string) => {
    onTabChange ? onTabChange(tabId) : setInternalActiveTab(tabId);
    handlePageChange(1);
  };

  const handlePageChange = (page: number) => {
    onPageChange ? onPageChange(page) : setInternalCurrentPage(page);
  };

  const handleSearch = (query: string) => {
    onSearch ? onSearch(query) : setInternalSearch(query);
    handlePageChange(1);
  };

  const handleFilterChangeInternal = (id: string, value: any) => {
    setFilterValues((prev) => ({ ...prev, [id]: value }));
    onFilterChange?.(id, value);
    handlePageChange(1);
  };

  const handleQuickFilterToggle = (id: string) => {
    setActiveQuickFilterIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
    handlePageChange(1);
  };

  const handleToggleColumn = (key: string) => {
    setVisibleColumnKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const handleItemsPerPageChange = (rows: number) => {
    if (onItemsPerPageChange) onItemsPerPageChange(rows);
    else setPageSize(rows);
    handlePageChange(1);
  };

  const handleSelectAll = (checked: boolean | 'indeterminate') => {
    if (checked === true) {
      const ids = selectAllScope === 'page'
        ? paginatedData.map(r => r.id)
        : selectAllScope === 'filtered'
          ? filteredData.map(r => r.id)
          : data.map(r => r.id);
      setSelectedRows(ids);
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (rowId: string) => {
    setSelectedRows((prev) => (prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]));
  };

  const handleRowExpand = (rowId: string) => {
    setExpandedRows((prev) => (prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]));
  };


  const handleFilterToggle = () => {
    if (onFilterToggle) onFilterToggle();
    else setFilterBarVisible((v) => !v);
  };

  const visibleColumns = React.useMemo(
    () => columns.filter((c) => visibleColumnKeys.includes(c.key)),
    [columns, visibleColumnKeys]
  );

  const searchFiltered = React.useMemo(
    () =>
      data.filter((row) =>
        visibleColumns.some((col) => String(row[col.key] ?? '').toLowerCase().includes(internalSearch.toLowerCase()))
      ),
    [data, visibleColumns, internalSearch]
  );

  const filtersApplied = React.useMemo(() => {
    const entries = Object.entries(filterValues).filter(([, v]) => v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0));
    return entries.length;
  }, [filterValues]);

  const tabPredicate = React.useMemo(() => tabs.find((t) => t.id === activeTab)?.predicate, [tabs, activeTab]);

  const filterBarFiltered = React.useMemo(() => {
    if (!filters?.length) return searchFiltered;
    return searchFiltered.filter((row) => {
      return Object.entries(filterValues).every(([id, value]) => {
        const def = filters.find((f) => f.id === id);
        if (!def || value === undefined || value === null || value === '') return true;
        if (def.predicate) return def.predicate(row, value);
        if (def.columnKey) {
          const cell = row[def.columnKey];
          if (typeof value === 'string') return String(cell ?? '').toLowerCase().includes(value.toLowerCase());
          return cell === value;
        }
        return true;
      });
    });
  }, [filters, filterValues, searchFiltered]);

  const quickFiltered = React.useMemo(() => {
    if (!quickFilters?.length || activeQuickFilterIds.length === 0) return filterBarFiltered;
    return filterBarFiltered.filter((row) =>
      activeQuickFilterIds.some((id) => quickFilters.find((q) => q.id === id)?.predicate(row))
    );
  }, [quickFilters, activeQuickFilterIds, filterBarFiltered]);

  const filteredData = React.useMemo(() => {
    if (!tabPredicate) return quickFiltered;
    return quickFiltered.filter((row) => tabPredicate(row));
  }, [quickFiltered, tabPredicate]);

  // Dynamic counts for each tab based on pre-tab filtered data
  const tabCounts = React.useMemo(() => {
    const base = quickFiltered;
    const entries = tabs.map((t) => [
      t.id,
      t.predicate ? base.filter((row) => t.predicate!(row)).length : base.length,
    ] as const);
    return Object.fromEntries(entries) as Record<string, number>;
  }, [tabs, quickFiltered]);

  const sortedData = React.useMemo(
    () =>
      [...filteredData].sort((a, b) => {
        const aValue = a[sortColumn];
        const bValue = b[sortColumn];
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      }),
    [filteredData, sortColumn, sortDirection]
  );

  const paginatedData = React.useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return sortedData.slice(startIndex, startIndex + pageSize);
  }, [sortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  return {
    activeTab,
    sortColumn,
    sortDirection,
    currentPage,
    internalSearch,
    filterValues,
    selectedRows,
    expandedRows,
    filterBarVisible,
    filteredData,
    sortedData,
    paginatedData,
    totalPages,
    handleSort,
    handleTabChange,
    handlePageChange,
    handleSearch,
    handleFilterChange: handleFilterChangeInternal,
    activeQuickFilterIds,
    handleQuickFilterToggle,
    handleSelectAll,
    handleRowSelect,
    handleRowExpand,
    handleFilterToggle,
    pageSize,
    handleItemsPerPageChange,
    visibleColumns,
    visibleColumnKeys,
    handleToggleColumn,
    filtersApplied,
    selectAllScope,
    selectAllTargetIds: (selectAllScope === 'page' ? paginatedData : selectAllScope === 'filtered' ? filteredData : data).map(r => r.id),
    tabCounts,
  } as const;
}
